import {
  IconButton,
  Select,
  styled,
  ToggleButton,
  MenuItem,
  Typography,
} from '@mui/material';
import { Expand } from '../../../icon/Expand';
import { Collapse } from '../../../icon/Collapse';
import { InformationCircle } from '../../../icon/InformationCircle';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import { typographyStyles } from '../DataGridScanStatusField/styles';
interface IWrapper {
  isLightBackground?: boolean;
  darkBackgroundColor: string;
  isAllScans?: boolean;
  isFullScreen?: boolean;
}

type ResultType = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  boxShadow?: string;
  borderRadius?: number;
  backgroundColor?: string;
  paddingBottom?: string;
  display?: string;
  flexDirection?: string;
  '& > :not(style) + :not(style)'?: {
    marginRight?: number;
    marginLeft?: number;
    marginTop?: number;
    marginBottom?: number;
  };
};

const LARGE_SCREEN_MIN_WIDTH = 2300;

export const Wrapper = styled('div')<IWrapper>(
  ({ isAllScans, isFullScreen, darkBackgroundColor, isLightBackground }) => ({
    width: '100%',
    height: isAllScans ? '90vh' : isFullScreen ? '87vh' : '67vh', // Default height for smaller screens
    position: 'relative',
    backgroundColor: isLightBackground ? '#f6f6f6' : darkBackgroundColor,
    textAlign: 'center',

    // Apply breakpoints only when isAllScans is false
    ...(isAllScans
      ? {}
      : {
          // Very large monitors/screens
          [`@media (min-width: ${LARGE_SCREEN_MIN_WIDTH}px)`]: {
            height: isFullScreen ? '90vh' : 'calc(75vh)',
          },

          // Very tall + not wide screens
          '@media (max-aspect-ratio: 4/3)': {
            height: isFullScreen ? '92vh' : 'calc(83vh)',
          },
        }),
  })
);

export const MeshViewerWrapper = styled('div')<{
  isAllScans?: boolean;
  isFullScreen?: boolean;
}>(({ theme, isAllScans, isFullScreen }) => ({
  position: 'relative',
  width: '100%',
  height: isAllScans ? '90vh' : isFullScreen ? '87vh' : '67vh', // Default height for smaller screens
  background: 'white',

  // Very large monitors/screens
  [`@media (min-width: ${LARGE_SCREEN_MIN_WIDTH}px)`]: {
    height: isFullScreen ? '90vh' : 'calc(75vh)',
  },

  // Very tall + not wide screens
  '@media (max-aspect-ratio: 4/3)': {
    height: isFullScreen ? '92vh' : 'calc(83vh)',
  },

  // [theme.breakpoints.up('xl')]: {
  //   height: isFullScreen ? '92vh' : '100%',
  // },
  // [theme.breakpoints.down('xl')]: {
  //   height: isFullScreen ? '92vh' : '100%',
  // },
  // [theme.breakpoints.down('lg')]: {
  //   height: isFullScreen ? '92vh' : '50vh',
  // },
}));

export const PositionedStack = styled('div')<{
  position: 'tl' | 'tr' | 'bl' | 'br';
  direction?: 'h' | 'v';
  variant?: 'custom' | 'default';
}>`
  position: absolute;
  box-shadow: 0px 0px 10px 3px rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  max-width: 50%;
  ${({ position, direction, variant }) => {
    const result: ResultType = {};
    if (position === 'tr') {
      result.top = 16;
      result.right = 16;
    } else if (position === 'br') {
      result.bottom = 16;
      result.right = 16;
    } else if (position === 'bl') {
      result.bottom = 16;
      result.left = 16;
    } else {
      // like TL
      result.top = 16;
      result.left = 16;
    }

    if (direction) {
      result.display = 'flex';
      if (direction === 'h') {
        if (position === 'tr' || position === 'br') {
          result.flexDirection = 'row-reverse';
          result['& > :not(style) + :not(style)'] = {
            marginRight: 12,
          };
        } else {
          result.flexDirection = 'row';
          result['& > :not(style) + :not(style)'] = {
            marginLeft: 12,
          };
        }
      } else {
        if (position === 'tr' || position === 'tl') {
          result.flexDirection = 'column';
          result['& > :not(style) + :not(style)'] = {
            marginTop: 12,
          };
        } else {
          result.flexDirection = 'column-reverse';
          result['& > :not(style) + :not(style)'] = {
            marginBottom: 12,
          };
        }
      }
    }

    if (variant === 'custom') {
      result.boxShadow = 'rgba(0, 0, 0, 0.2) 0px 0px 10px 3px';
      result.borderRadius = 8;
      result.backgroundColor = 'white';
      result.paddingBottom = '0.5vh';
    }

    return result as string;
  }}
`;

export const ViewModeSelect = styled(Select)`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  box-shadow: 0px 0px 10px 3px rgba(0, 0, 0, 0.2);

  & .MuiSelect-select {
    padding: 4px 16px;
    background: #fff;
    background-color: white;
    border-radius: 10px;
    font-weight: 400;
    font-size: 16px;
    line-height: 145%;
    color: #353535;
    max-width: 10rem;
    border: none;
  }
`;

export const SyncModeSelect = styled(ViewModeSelect)<{
  alternateSize?: boolean;
  alternateMargin?: number;
}>(({ theme, alternateSize, alternateMargin }) => ({
  position: 'relative',
  textAlign: 'center',
  boxShadow: 'none',
  top: 0,
  right: 0,
  height: '2.5rem',

  '& .MuiSelect-select': {
    background: '#f6f6f6',
    backgroundColor: '#f6f6f6',
    color: '#000',

    ...typographyStyles(theme, alternateSize, alternateMargin),
  },
}));

export const HeaderContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  height: 2rem;
`;

export const RowContainer = styled('div')`
  display: flex;
  align-items: center;
  > label {
    margin: 0 0.25rem;
  }
`;

export const Title = styled(Typography)<{
  alternateSize?: boolean;
  alternateMargin?: number;
}>(({ theme, alternateSize, alternateMargin }) => ({
  ...typographyStyles(theme, alternateSize, alternateMargin),
}));

export const ControlsButton = styled('button')(({ theme }) => ({
  border: 'none',
  cursor: 'pointer',
  borderRadius: '8px',
  color: 'black',
  backgroundColor: '#f6f6f6',
  height: '2.5rem',
  ...typographyStyles(theme),
}));

export const ExpandIcon = styled(Expand)<{
  alternateSize?: boolean;
  alternateMargin?: number;
}>(({ theme, alternateSize, alternateMargin }) => ({
  ...typographyStyles(theme, alternateSize, alternateMargin),
  cursor: 'pointer',
  marginLeft: '0.75rem',
  '&:hover': {
    opacity: 0.5,
  },
}));

export const CollapseIcon = styled(Collapse)<{
  alternateSize?: boolean;
  alternateMargin?: number;
}>(({ theme, alternateSize, alternateMargin }) => ({
  ...typographyStyles(theme, alternateSize, alternateMargin),
  cursor: 'pointer',
  marginLeft: '0.75rem',
  '&:hover': {
    opacity: 0.5,
  },
}));

export const InformationCircleIcon = styled(InformationCircle)`
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 5px;
  margin-right: 0.25rem;
  &:hover {
    opacity: 0.5;
  }
`;

export const Analysis3DImageContainer = styled('div')`
  display: flex;
  position: relative;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const Analysis3dPngImage = styled('img')`
  position: absolute;
  height: 100%;
  padding: 3rem;
`;

export const AsymmetryPngImage = styled('img')`
  height: 100%;
  padding: 40px 0;
`;

export const LayersContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: #ffffff;
  padding: 0.5rem;
  border-radius: 8px;
`;

export const LayerRow = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const EmptyView = styled('div')<{
  isFullScreen?: boolean;
  isAllScans?: boolean;
}>`
  height: ${({ isFullScreen, isAllScans }) =>
    isAllScans ? '90vh' : isFullScreen ? '90vh' : '67vh'};
  @media (min-width: ${LARGE_SCREEN_MIN_WIDTH}px) {
    height: ${({ isFullScreen, isAllScans }) =>
      isAllScans || isFullScreen ? '90vh' : '75vh'};
  }
  @media (max-aspect-ratio: 4/3) {
    height: ${({ isFullScreen, isAllScans }) =>
      isAllScans || isFullScreen ? '90vh' : '83vh'};
  }
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: darkgrey;
`;

export const LoaderContainer = styled('div')`
  position: absolute;
  background-color: rgba(246, 246, 246, 0);
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface IInfoBadge {
  backgroundColor?: string;
}

export const InfoBadge = styled('div')<IInfoBadge>`
  min-width: 32px;
  min-height: 32px;
  background: ${(props) =>
    props.backgroundColor ? props.backgroundColor : '#FFFFFF'};
  border-radius: 8px;
  color: #353535;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 145%;
`;

export const IdBadge = styled(InfoBadge)`
  padding: 4px 11px;
  text-align: left;
  display: flex;
  flex-direction: column;
`;

export const ViewerIconButton = styled(IconButton)`
  padding: 6px;
  font-size: 20px;
  border-radius: 8px;
  background: #ffffff;
  color: #353535;

  &:hover {
    background: #ffffffcc;
  }
`;

export const MeshLegendIcons = styled('img')`
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
  margin: 0.25rem;
`;

export const DeviationMeshViewerControlPanelWrapper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

export const DeviationMeshViewerControlPanel = styled('div')`
  position: absolute;
  bottom: 2%;
  width: 96%;
  height: fit-content;
  background-color: white;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 0px 10px 3px;
`;

export const SyncModeToggleButton = styled(ToggleButton)<{
  active?: boolean;
}>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '0.25rem',
  flexDirection: 'row',
  height: '2.5rem',
  backgroundColor: '#f6f6f6',
  color: '#878787',
  borderColor: '#f6f6f6',
  borderRadius: '10px',
  fontWeight: 700,
  cursor: 'pointer',
  '&.Mui-selected': {
    backgroundColor: '#353535',
    color: '#fff',
    borderColor: '#353535',
    '&:hover': {
      backgroundColor: '#353535',
      color: '#fff',
      borderColor: '#353535',
    },
  },
  transition: theme.transitions.create(['background', 'color']),
  ...typographyStyles(theme, true),
}));

export const CustomSyncIcon = styled(SyncIcon)(({ theme }) => ({
  ...typographyStyles(theme),
}));

export const CustomSyncDisabledIcon = styled(SyncDisabledIcon)(({ theme }) => ({
  ...typographyStyles(theme),
}));

export const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  ...typographyStyles(theme, true),
}));
